import { v4 as uuidv4 } from 'uuid'
import { Message, MentorAIRequest } from '../types/mentorTypes'
import { CommandChannelClient } from '../pages/meeting-view/types/video-types'
import { CommandChannelAction } from '../pages/meeting-view/hooks/useCommandChannel'

/**
 * Create a user message
 * @param content - The content of the message
 * @returns A new user message object
 */
export const createUserMessage = (content: string): Message => {
    return {
        id: `user-${uuidv4()}`,
        content,
        sender: 'user',
        timestamp: Date.now()
    }
}

/**
 * Create a mentor message
 * @param content - The content of the message
 * @returns A new mentor message object
 */
export const createMentorMessage = (content: string): Message => {
    return {
        id: `mentor-${uuidv4()}`,
        content,
        sender: 'mentor',
        timestamp: Date.now()
    }
}

/**
 * Create a welcome message
 * @returns A new welcome message object
 */
export const createWelcomeMessage = (): Message => {
    return {
        id: `welcome-${uuidv4()}`,
        content: "Hi there! I'm your Mentor AI. I can help you understand the content better or answer any questions you might have.",
        sender: 'mentor',
        timestamp: Date.now()
    }
}

/**
 * Broadcast a message if the current user is the content sharer
 * @param message - The message to broadcast
 * @param commandChannel - The command channel to use
 * @param isContentSharer - Whether the current user is the content sharer
 */
export const broadcastIfSharer = (
    message: CommandChannelAction,
    commandChannel: CommandChannelClient,
    isContentSharer: boolean,
): void => {
    if (isContentSharer) {
        commandChannel.send(JSON.stringify(message))
    }
}

/**
 * Create a Mentor AI request
 * @param chatId - The ID of the chat
 * @param stepId - The ID of the step
 * @param question - The question to ask
 * @param members - The participants in the session
 * @param context - The context for the question
 * @returns A new Mentor AI request object
 */
export const createMentorAIRequest = (
    stepId: string,
    question: string,
    members: any[],
    chatId: string,
    context?: string[],
): MentorAIRequest => {
    // Map Zoom participants to the format expected by the API
    const mappedMembers = members.map(member => {
        // Extract first and last name from displayName if available
        let firstName = '';
        let lastName = '';
        
        if (member.displayName) {
            const nameParts = member.displayName.split(' ');
            firstName = nameParts[0] || '';
            lastName = nameParts.slice(1).join(' ') || '';
        }
        
        return {
            // Use userIdentity as _profileId - this is required by the API
            _profileId: member.userIdentity || '',
            // Use existing name/lastname if available, otherwise use extracted values
            name: member.name || firstName,
            lastname: member.lastname || lastName,
            // Always include displayName
            displayName: member.displayName || `${firstName} ${lastName}`.trim()
        };
    });
    
    return {
        chatId,
        stepId,
        question,
        context: {fragment: context || []},
        members: mappedMembers,
        timestamp: Date.now()
    }
}